import { useEffect, useMemo, useState } from "react";
import {
  fetchVehicleModelCopyAdminDetail,
  publishVehicleModelCopy,
  saveVehicleModelCopyDraft,
  unpublishVehicleModelCopy,
} from "../../../../services/vehicleModelCopyAdminApi";
import VehicleModelCopyHistoryPanel from "./VehicleModelCopyHistoryPanel";

const LOCALES = ["en-US", "es-CO", "ru-RU"];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function localeStatusLabel(state) {
  if (!state) return "Missing";
  if (state.status === "published") return "Published";
  if (state.status === "draft") return "Draft";
  return "Missing";
}

function publicSourceLabel(source) {
  switch (source) {
    case "db_exact":
      return "Published DB copy";
    case "db_alias":
      return "DB alias match";
    case "published_db":
      return "Published DB copy";
    default:
      return "No description";
  }
}

const VehicleModelCopyEditorPanel = ({
  selection,
  canEdit,
  canPublish,
  onClose,
  onUpdated,
}) => {
  const [detail, setDetail] = useState(null);
  const [activeLocale, setActiveLocale] = useState("es-CO");
  const [draftBody, setDraftBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [dirtyLocales, setDirtyLocales] = useState({});
  const [activePanel, setActivePanel] = useState("editor");

  const localeState = detail?.locales?.[activeLocale] || null;
  const isDirty = Boolean(dirtyLocales[activeLocale]);

  const loadDetail = async () => {
    if (!selection) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const nextDetail = await fetchVehicleModelCopyAdminDetail({
        vehicleModelId: selection.vehicleModelId,
        makeKey: selection.makeKey,
        modelKey: selection.modelKey,
      });
      setDetail(nextDetail);
      const locale = nextDetail.locales?.[activeLocale];
      setDraftBody(locale?.body || "");
      setDirtyLocales({});
    } catch (error) {
      setErrorMessage(error.message || "Unable to load copy detail.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [selection?.vehicleModelId, selection?.makeKey, selection?.modelKey]);

  useEffect(() => {
    if (!detail) return;
    const locale = detail.locales?.[activeLocale];
    if (!dirtyLocales[activeLocale]) {
      setDraftBody(locale?.body || "");
    }
  }, [activeLocale, detail, dirtyLocales]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const switchLocale = (locale) => {
    if (locale === activeLocale) return;
    if (isDirty) {
      const proceed = window.confirm(
        "You have unsaved changes for this locale. Switch tabs without saving?"
      );
      if (!proceed) return;
      setDirtyLocales((current) => {
        const next = { ...current };
        delete next[activeLocale];
        return next;
      });
    }
    setActiveLocale(locale);
  };

  const handleSaveDraft = async () => {
    if (!canEdit || !detail) return;
    if (!draftBody.trim()) {
      setErrorMessage("Description body cannot be empty.");
      return;
    }
    try {
      setSaving(true);
      setErrorMessage("");
      await saveVehicleModelCopyDraft({
        vehicleModelId: detail.vehicleModelId,
        makeKey: detail.makeKey,
        modelKey: detail.modelKey,
        locale: activeLocale,
        body: draftBody,
        expectedUpdatedAt: localeState?.expectedUpdatedAt || null,
      });
      setToast("Draft saved.");
      setDirtyLocales((current) => {
        const next = { ...current };
        delete next[activeLocale];
        return next;
      });
      await loadDetail();
      onUpdated?.();
    } catch (error) {
      if (error.status === 409) {
        setErrorMessage(
          "This description changed after you opened it. Reload the latest version before saving."
        );
      } else {
        setErrorMessage(error.message || "Draft save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!canPublish || !localeState?.copyId) return;
    try {
      setPublishing(true);
      setErrorMessage("");
      await publishVehicleModelCopy(localeState.copyId, {
        expectedUpdatedAt: localeState.expectedUpdatedAt,
      });
      setToast("Copy published.");
      setShowPublishConfirm(false);
      await loadDetail();
      onUpdated?.();
    } catch (error) {
      if (error.status === 409) {
        setErrorMessage(
          "This description changed after you opened it. Reload the latest version before publishing."
        );
      } else {
        setErrorMessage(error.message || "Publish failed.");
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!canPublish || !localeState?.copyId) return;
    try {
      setPublishing(true);
      setErrorMessage("");
      await unpublishVehicleModelCopy(localeState.copyId, {
        expectedUpdatedAt: localeState.expectedUpdatedAt,
      });
      setToast("Copy unpublished.");
      setShowUnpublishConfirm(false);
      await loadDetail();
      onUpdated?.();
    } catch (error) {
      if (error.status === 409) {
        setErrorMessage(
          "This description changed after you opened it. Reload the latest version before unpublishing."
        );
      } else {
        setErrorMessage(error.message || "Unpublish failed.");
      }
    } finally {
      setPublishing(false);
    }
  };

  const previewLabel = useMemo(() => {
    if (!localeState) return "Preview";
    if (localeState.status === "published") return "Currently published";
    if (isDirty || localeState.status === "draft") return "Draft preview";
    return "Preview";
  }, [isDirty, localeState]);

  if (!selection) return null;

  return (
    <section className="panel-card admin-vehicle-model-copy-editor" aria-label="Vehicle model copy editor">
      <header className="admin-vehicle-model-copy-editor__header">
        <div>
          <h2>
            {detail?.makeName || selection.makeName} {detail?.modelName || selection.modelName}
          </h2>
          <p className="muted">
            {detail?.makeKey || selection.makeKey} / {detail?.modelKey || selection.modelKey}
            {detail?.vehicleModelId ? ` · ${detail.vehicleModelId}` : " · Unlinked"}
          </p>
        </div>
        <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
          Close
        </button>
      </header>

      {toast ? (
        <p className="admin-dashboard__toast" role="status">
          {toast}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="admin-state-panel admin-state-panel--error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {loading ? <p className="muted">Loading copy detail…</p> : null}

      {!loading && detail ? (
        <>
          <div className="admin-vehicle-model-copy-editor__meta">
            <p>
              Public source:{" "}
              <strong>
                {publicSourceLabel(localeState?.publicResolution?.effectivePublicSource)}
              </strong>
            </p>
          </div>

          <div className="admin-vehicle-model-copy-editor__tabs" role="tablist" aria-label="Editor panels">
            <button
              type="button"
              role="tab"
              aria-selected={activePanel === "editor"}
              className={`admin-vehicle-model-copy-editor__tab${
                activePanel === "editor" ? " admin-vehicle-model-copy-editor__tab--active" : ""
              }`}
              onClick={() => setActivePanel("editor")}
            >
              Editor
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activePanel === "history"}
              className={`admin-vehicle-model-copy-editor__tab${
                activePanel === "history" ? " admin-vehicle-model-copy-editor__tab--active" : ""
              }`}
              onClick={() => setActivePanel("history")}
            >
              History
            </button>
          </div>

          <div className="admin-vehicle-model-copy-editor__tabs" role="tablist" aria-label="Locales">
            {LOCALES.map((locale) => {
              const state = detail.locales?.[locale];
              const selected = locale === activeLocale;
              return (
                <button
                  key={locale}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`admin-vehicle-model-copy-editor__tab${
                    selected ? " admin-vehicle-model-copy-editor__tab--active" : ""
                  }`}
                  onClick={() => switchLocale(locale)}
                >
                  {locale}
                  <span className="admin-vehicle-model-copy-editor__tab-status">
                    {localeStatusLabel(state)}
                  </span>
                </button>
              );
            })}
          </div>

          {activePanel === "history" ? (
            <VehicleModelCopyHistoryPanel
              copyId={localeState?.copyId}
              canRestore={canEdit}
              expectedUpdatedAt={localeState?.expectedUpdatedAt}
              onRestored={async () => {
                await loadDetail();
                onUpdated?.();
              }}
            />
          ) : (
          <div className="admin-vehicle-model-copy-editor__grid">
            <div>
              <h3>Published copy</h3>
              <pre className="admin-vehicle-model-copy-editor__readonly">
                {localeState?.publishedBody || "—"}
              </pre>
              <p className="muted">
                Published: {formatDate(localeState?.publishedAt)} · Updated:{" "}
                {formatDate(localeState?.updatedAt)} · {localeState?.updatedBy?.displayName || "—"}
              </p>
            </div>

            <div>
              <h3>Draft editor</h3>
              <label className="sr-only" htmlFor="vehicle-model-copy-draft-body">
                Draft body for {activeLocale}
              </label>
              <textarea
                id="vehicle-model-copy-draft-body"
                className="admin-vehicle-model-copy-editor__textarea"
                rows={12}
                value={draftBody}
                disabled={!canEdit || saving || publishing}
                onChange={(event) => {
                  setDraftBody(event.target.value);
                  setDirtyLocales((current) => ({ ...current, [activeLocale]: true }));
                }}
              />
              <p className="muted">{draftBody.length} characters</p>
              <div className="admin-vehicle-model-copy-editor__actions">
                <button
                  type="button"
                  className="admin-dashboard__action-btn"
                  disabled={!canEdit || saving || publishing || !isDirty}
                  onClick={handleSaveDraft}
                >
                  {saving ? "Saving…" : "Save draft"}
                </button>
                {canPublish && localeState?.status === "draft" ? (
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                    disabled={publishing || isDirty}
                    onClick={() => setShowPublishConfirm(true)}
                  >
                    Publish
                  </button>
                ) : null}
                {canPublish && localeState?.status === "published" ? (
                  <button
                    type="button"
                    className="admin-dashboard__action-btn"
                    disabled={publishing}
                    onClick={() => setShowUnpublishConfirm(true)}
                  >
                    Unpublish
                  </button>
                ) : null}
              </div>
            </div>

            <div>
              <h3>{previewLabel}</h3>
              <div className="admin-vehicle-model-copy-editor__preview">
                <p className="admin-vehicle-model-copy-editor__preview-heading">
                  {detail.makeName} {detail.modelName}
                </p>
                <p>{isDirty ? draftBody : localeState?.publishedBody || draftBody || "—"}</p>
              </div>
            </div>
          </div>
          )}
        </>
      ) : null}

      {showPublishConfirm ? (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="panel-card admin-vehicle-model-copy-editor__confirm">
            <h3>Publish {activeLocale} copy?</h3>
            <p>
              {detail?.makeName} {detail?.modelName} ({detail?.makeKey}/{detail?.modelKey})
            </p>
            <p className="muted">Current published excerpt:</p>
            <pre className="admin-vehicle-model-copy-editor__readonly">
              {(localeState?.publishedBody || "—").slice(0, 240)}
            </pre>
            <p className="muted">New draft excerpt:</p>
            <pre className="admin-vehicle-model-copy-editor__readonly">
              {(draftBody || "—").slice(0, 240)}
            </pre>
            <div className="admin-vehicle-model-copy-editor__actions">
              <button type="button" className="admin-dashboard__action-btn" onClick={() => setShowPublishConfirm(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                disabled={publishing}
                onClick={handlePublish}
              >
                {publishing ? "Publishing…" : "Confirm publish"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showUnpublishConfirm ? (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="panel-card admin-vehicle-model-copy-editor__confirm">
            <h3>Unpublish {activeLocale} copy?</h3>
            <p>
              Unpublishing removes this locale from published DB reads. Runtime resolution may then use
              another approved DB locale fallback or no public description.
            </p>
            <div className="admin-vehicle-model-copy-editor__actions">
              <button
                type="button"
                className="admin-dashboard__action-btn"
                onClick={() => setShowUnpublishConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                disabled={publishing}
                onClick={handleUnpublish}
              >
                {publishing ? "Unpublishing…" : "Confirm unpublish"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default VehicleModelCopyEditorPanel;
